import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  QueryList,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { combineLatest, merge, Observable, of } from 'rxjs';
import {
  map,
  startWith,
  switchMap,
  switchMapTo,
  takeUntil,
} from 'rxjs/operators';
import { InputComponent } from '../input/public-api';
import { coerceString } from '../utils/coercion';
import { BaseSelect } from './base-select';
import { OptionComponent } from './option/option.component';

@Component({
  selector: 'aui-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
    {
      provide: BaseSelect,
      useExisting: SelectComponent,
    },
  ],
})
export class SelectComponent extends BaseSelect<any>
  implements AfterContentInit {
  @ViewChild('inputRef')
  inputRef: InputComponent;

  displayText = '';

  values$: Observable<any[]> = this.value$$
    .asObservable()
    .pipe(map(val => [val]));

  get rootClass() {
    return `aui-select aui-select--${this.size} ${
      this.displayClearable() ? 'isClearable' : ''
    }`;
  }

  constructor(cdr: ChangeDetectorRef) {
    super(cdr);
  }

  ngAfterContentInit() {
    super.ngAfterContentInit();
    this.contentOptions.changes
      .pipe(
        startWith(this.contentOptions),
        takeUntil(this.destroy$$),
        switchMap(
          (options: QueryList<OptionComponent>) =>
            options.length
              ? combineLatest(
                  options.map(option => merge(option.value$, option.label$)),
                )
              : of([]),
        ),
        switchMapTo(this.value$),
      )
      .subscribe(value => {
        this.updateSelectDisplay(value);
        this.cdr.markForCheck();
      });
  }

  onShowOptions() {
    super.onShowOptions();
    this.inputRef.elementRef.nativeElement.focus();
  }

  getPlaceholder() {
    return this.filterable && this.opened ? this.displayText : this.placeholder;
  }

  getInputValue() {
    return this.filterable && this.opened ? '' : this.displayText;
  }

  displayClearable() {
    return !this.disabled && this.clearable && this.getInputValue();
  }

  writeValue(val: any) {
    this.value$$.next(val);
    this.updateSelectDisplay(val);
    this.closeOption();
    this.cdr.detectChanges();
  }

  selectOption(option: OptionComponent) {
    this.emitValueChange(option.value);
    if (this.onChange) {
      this.closeOption();
    }
  }

  clearValue(event: Event) {
    this.emitValueChange(null);
    event.stopPropagation();
    event.preventDefault();
  }

  updateSelectDisplay(value: any) {
    const val = value;
    const pickedOption = this.contentOptions
      ? this.contentOptions.find(
          option => this.trackFn(option.value) === this.trackFn(val),
        )
      : null;
    if (pickedOption) {
      this.displayText = pickedOption.label || this.trackFn(pickedOption.value);
    } else {
      this.displayText = coerceString(val ? this.trackFn(val) : val);
    }
  }
}
